export type WebContext = {
  sessionId: string;
  generation: number;
  holder: string;
  csrf: string;
  namespace: string;
  apiId: number;
  wasActive: boolean;
  apiHash: string;
  deviceModel: string;
  expectedUserId: string;
  accountTitle: string;
};

export function readContext(): WebContext {
  const main = (globalThis as typeof globalThis & { tgready?: WebContext }).tgready;
  if (main) return main;
  const encoded = new URLSearchParams((self as unknown as { name: string }).name || '').get(
    'tgready',
  );
  if (!encoded) throw new Error('TGREADY_CONTEXT_REQUIRED');
  return JSON.parse(encoded) as WebContext;
}

export async function requestWeb(path: string, body?: unknown, method = 'POST'): Promise<any> {
  const c = readContext();
  const response = await fetch(`/api/telegram-web/sessions/${c.sessionId}/${path}`, {
    method,
    credentials: 'same-origin',
    signal: AbortSignal.timeout(15000),
    headers: {
      'content-type': 'application/json',
      'x-csrf-token': c.csrf,
      'x-web-holder': c.holder,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`TGREADY_REQUEST_${response.status}`);
  return response.json();
}

export async function approveChallenge(qr: { token: Uint8Array; expires: number }) {
  const token = btoa(String.fromCharCode(...qr.token))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return requestWeb('challenge', { id: crypto.randomUUID(), token, expires: qr.expires });
}

export async function confirmIdentity(id: string) {
  const c = readContext();
  if (id !== c.expectedUserId) {
    await requestWeb('end');
    throw new Error('TGREADY_IDENTITY_MISMATCH');
  }
  // Node approval and the browser's login update may arrive in either order.
  for (let attempt = 0; attempt < 15; attempt++) {
    try {
      const result = await requestWeb('ready', { user_id: id });
      if (result.state !== 'active') throw new Error('TGREADY_NOT_ACTIVE');
      return;
    } catch (error) {
      if (attempt === 14) throw error;
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });
    }
  }
}

export function getTarget(ip: string, port: number, isTest: boolean, premium: boolean) {
  const match = /^zws([1-5])(-1)?\.web\.telegram\.org$/.exec(ip);
  if (!match || port !== 443 || isTest) throw new Error('TGREADY_DC_REJECTED');
  return `${match[1]}${match[2] || ''}${premium ? '-premium' : ''}`;
}

export function getTunnelUrl() {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/telegram-web/tunnel`;
}

export function authenticateSocket(
  socket: WebSocket,
  ip: string,
  port: number,
  isTest: boolean,
  premium: boolean,
) {
  const c = readContext();
  return new Promise<void>((resolve, reject) => {
    const fail = () => {
      reject(new Error('TGREADY_TUNNEL_CLOSED'));
    };
    socket.addEventListener('close', fail, { once: true });
    socket.onmessage = (event) => {
      try {
        if (typeof event.data !== 'string' || JSON.parse(event.data).ready !== true) {
          throw new Error();
        }
        socket.removeEventListener('close', fail);
        resolve();
      } catch {
        socket.close();
        reject(new Error('TGREADY_TUNNEL_REJECTED'));
      }
    };
    socket.send(
      JSON.stringify({
        session_id: c.sessionId,
        holder: c.holder,
        csrf: c.csrf,
        target: getTarget(ip, port, isTest, premium),
      }),
    );
  });
}
